import { withNamespaces } from "react-i18next";
import Accordion from 'react-bootstrap/Accordion';
import { Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { RiCheckLine, RiCloseLine, RiInformationLine, RiMoneyDollarCircleLine, RiQuestionLine } from "react-icons/ri";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';

const FormatPrice = ({
    t, 
    currency, 
    value
}) => {
    if (value === null || value === undefined) {
        return '—'
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 2
    }).format(value)
}

const FinancialSummary = ({
    t,
    setActiveAccordion,
    activeAccordion,
    detailsQuery
}) => {
    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const {
        quickcheck,
        previous_sales,
        net_income,
        total_assets,
        liquid_assets,
        inventory,
        total_liabilities,
        accounts_payable,
        accounts_receivable,
        total_current_assets,
        total_current_liabilities,
        previous_net_worth,
        accounts_audited,
        statement_consolidated,
        previous_statement_date,
        figures_estimated,
        year_period
    } = organizationData || {};

    const dateUtils = new DateUtils();

    return (
        <Accordion.Item 
            onClick={() => setActiveAccordion('financial-summary')}
            className={activeAccordion === 'financial-summary' ? 'active' : ''}
            eventKey={'financial-summary'}
        >
            <Accordion.Header>
                <RiMoneyDollarCircleLine size="24" className="me-2" />
                <span>
                    {t('Financial Summary')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <Row>
                    <Col lg="6">
                        <Table striped bordered hover size="sm">
                            <tbody>
                                <tr>
                                    <th style={{
                                        width: '40%'
                                    }}>{t('Revenue (Current)')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={quickcheck.sales}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Revenue (Previous)')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={previous_sales}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Net Income')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={net_income}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Net Worth')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={quickcheck.net_worth}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Total Assets')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={total_assets}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Liquid Assets')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={liquid_assets}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Inventory')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={inventory}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Intangible Assets')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={quickcheck.intangible_assets}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Total Liabilities')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={total_liabilities}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Accounts Payable')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={accounts_payable}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Accounts Receivable')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={accounts_receivable}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>

                    <Col lg="6">
                        <Table striped bordered hover size="sm">
                            <tbody>
                                <tr>
                                    <th>{t('Total Current Assets')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={total_current_assets}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Total Current Liabilities')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={total_current_liabilities}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Previous Net Worth')}</th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            t={t}
                                            value={previous_net_worth}
                                            currency={quickcheck.currency}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Accounts Audited')}
                                        <span
                                            id="tooltip-financial_accounts_audited"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-financial_accounts_audited"
                                        >
                                            {t('Indicates whether D&B has been able to determine whether the financial statement was prepared by audit.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td>
                                        {accounts_audited === true ? (
                                            <>
                                                <RiCheckLine size={20} className="text-primary" />
                                                {t('Yes')}
                                            </>
                                        ) : accounts_audited === false ? (
                                            <>
                                                <RiCloseLine size={20} className="text-danger" />
                                                {t('No')}
                                            </>
                                        ) : (
                                            <>
                                                <RiQuestionLine size={20} className="text-secondary" />
                                                {t('Unknown')}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Statement Consolidated')}</th>
                                    <td>
                                        {statement_consolidated === true ? (
                                            <>
                                                <RiCheckLine size={20} className="text-primary" />
                                                {t('Yes')}
                                            </>
                                        ) : statement_consolidated === false ? (
                                            <>
                                                <RiCloseLine size={20} className="text-danger" />
                                                {t('No')}
                                            </>
                                        ) : (
                                            <>
                                                <RiQuestionLine size={20} className="text-secondary" />
                                                {t('Unknown')}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Income Statement Date')}</th>
                                    <td>
                                        {quickcheck.income_statement_date ? (
                                            dateUtils.convertDateToDate(quickcheck.income_statement_date, INTERNATIONAL_DATE_FORMAT)
                                        ) : t('N/A')}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Income Statement End Date')}</th>
                                    <td>
                                        {quickcheck.income_statement_date_end ? (
                                            dateUtils.convertDateToDate(quickcheck.income_statement_date_end, INTERNATIONAL_DATE_FORMAT)
                                        ) : t('N/A')}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Financial Statement Date')}</th>
                                    <td>
                                        {quickcheck.financial_statement_date ? (
                                            dateUtils.convertDateToDate(quickcheck.financial_statement_date, INTERNATIONAL_DATE_FORMAT)
                                        ) : t('N/A')}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Previous Statement Date')}</th>
                                    <td>
                                        {previous_statement_date ? (
                                            dateUtils.convertDateToDate(previous_statement_date, INTERNATIONAL_DATE_FORMAT)
                                        ) : t('N/A')}
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Figures Estimated')}
                                        <span
                                            id="tooltip-financial_figures_estimated"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-financial_figures_estimated"
                                        >
                                            {t('Indicates whether the financial statement figures are estimated or measured/actual.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td>
                                        {figures_estimated === true ? (
                                            <>
                                                <RiCheckLine size={20} className="text-primary" />
                                                {t('Yes')}
                                            </>
                                        ) : figures_estimated === false ? (
                                            <>
                                                <RiCloseLine size={20} className="text-danger" />
                                                {t('No')}
                                            </>
                                        ) : (
                                            <>
                                                <RiQuestionLine size={20} className="text-secondary" />
                                                {t('Unknown')}
                                            </>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th>{t('Annual Period')}</th>
                                    <td>
                                        {year_period === true ? (
                                            <>
                                                <RiCheckLine size={20} className="text-primary" />
                                                {t('Yes')}
                                            </>
                                        ) : year_period === false ? (
                                            <>
                                                <RiCloseLine size={20} className="text-danger" />
                                                {t('No')}
                                            </>
                                        ) : (
                                            <>
                                                <RiQuestionLine size={20} className="text-secondary" />
                                                {t('Unknown')}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    FinancialSummary
);